import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../services/api';
import { UserIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { User } from '../contexts/AuthContext';

// Helper function to format role display
const formatRoleDisplay = (role: string): string => {
  switch (role) {
    case 'eskimi_admin':
      return 'Eskimi User';
    case 'adops':
      return 'AdOps';
    case 'admin':
      return 'Admin';
    case 'user':
      return 'User';
    default:
      return role;
  }
};

interface UserSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserSwitcher: React.FC<UserSwitcherProps> = ({ isOpen, onClose }) => {
  const { startImpersonation, impersonation, user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const users = await userApi.getAllUsers();
      // Filter out the current admin user when not impersonating
      const filteredUsers = users.filter((u: User) => {
        if (impersonation?.active) {
          // When impersonating, show all users except the current impersonated user
          return u.id !== currentUser?.id;
        } else {
          // When not impersonating, show all users except self
          return u.id !== currentUser?.id;
        }
      });
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartImpersonation = async () => {
    if (!selectedUserId) return;

    try {
      await startImpersonation(selectedUserId);
      onClose();
    } catch (error) {
      console.error('Failed to start impersonation:', error);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <h2 className="text-xl font-semibold mb-4">Switch User View</h2>
        
        {/* Search bar */}
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ink-muted" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-line rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
          />
        </div>

        {/* Users list */}
        <div className="overflow-y-auto max-h-96 mb-4">
          {loading ? (
            <div className="text-center py-8">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-ink-muted">No users found</div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((user) => (
                <label
                  key={user.id}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedUserId === user.id
                      ? 'border-accent bg-nav-active'
                      : 'border-line hover:bg-ghost-bg'
                  }`}
                >
                  <input
                    type="radio"
                    name="user"
                    value={user.id}
                    checked={selectedUserId === user.id}
                    onChange={() => setSelectedUserId(user.id)}
                    className="mr-3"
                  />
                  <UserIcon className="h-5 w-5 text-ink-muted mr-3" />
                  <div className="flex-1">
                    <div className="font-medium">{user.name}</div>
                    <div className="text-sm text-ink-muted">{user.email}</div>
                  </div>
                  <div className="text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      user.role === 'admin'
                        ? 'bg-accent/10 text-accent-active'
                        : user.role === 'eskimi_admin' || user.role === 'adops'
                        ? 'bg-nav-active text-accent'
                        : 'bg-ghost-bg text-ink'
                    }`}>
                      {formatRoleDisplay(user.role)}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-ink-muted hover:text-ink transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleStartImpersonation}
            disabled={!selectedUserId}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-active disabled:bg-line disabled:cursor-not-allowed transition-colors"
          >
            Start Impersonation
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSwitcher;